import React, { lazy } from 'react';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import FunctionsIcon from '@mui/icons-material/Functions';

const DefaultResourceViewer = lazy(() => import('../resources/viewer/viewers/DefaultResourceViewer'));
const UnitResourceViewer = lazy(() => import('../resources/viewer/viewers/UnitResourceViewer'));
const FileResourceViewer = lazy(() => import('../resources/viewer/viewers/FileResourceViewer'));
const ChatSessionsDashboardViewer = lazy(() => import('../resources/viewer/viewers/ChatSessionsDashboardViewer'));
const CalendarsDashboardViewer = lazy(() => import('../resources/viewer/viewers/CalendarsDashboardViewer'));
const CalendarResourceViewer = lazy(() => import('../resources/viewer/viewers/CalendarResourceViewer'));
const JSXGraphViewer = lazy(() => import('../resources/viewer/viewers/JSXGraphViewer'));

const CONTENT_TYPE_REGISTRY = new Map();

const CORE_CONTENT_TYPES = {
  default: {
    viewer: DefaultResourceViewer,
    icon: { component: DescriptionIcon },
    getTitle: (resource) => resource?.title || resource?.name || resource?.original_filename || `Resource ${resource?.id?.substring(0, 8) || ''}`,
    handler: {
      needsFullFetch: () => false,
      processFetchedData: (fetchedData, originalData) => fetchedData,
    },
  },
  collection: {
    viewer: UnitResourceViewer,
    icon: { component: FolderIcon, props: { color: 'primary' } },
    getTitle: (resource) => resource?.title || 'Untitled Folder',
    handler: {
      needsFullFetch: (resource) => !resource.description || !Array.isArray(resource.children),
    }
  },
  unit: {
    viewer: UnitResourceViewer,
    icon: { component: FolderIcon, props: { color: 'primary' } },
    getTitle: (resource) => resource?.title || 'Untitled Folder',
    handler: {
      needsFullFetch: (resource) => !resource.description || !Array.isArray(resource.children),
    }
  },
  file: {
    viewer: FileResourceViewer,
    icon: { component: AttachFileIcon, props: { color: 'action' } },
    getTitle: (resource) => resource?.title || resource?.original_filename || 'Untitled File',
  },
  calendar: {
    viewer: CalendarResourceViewer,
    icon: { component: CalendarMonthIcon, props: { color: 'secondary' } },
    getTitle: (resource) => resource?.title || 'Untitled Calendar',
  },
  calendars_dashboard: {
    viewer: CalendarsDashboardViewer,
    icon: { component: CalendarMonthIcon, props: { color: 'secondary' } },
    getTitle: () => 'My Calendars',
  },
  chat_sessions_dashboard: {
    viewer: ChatSessionsDashboardViewer,
    icon: { component: FolderIcon, props: { color: 'primary' } },
    getTitle: () => 'Chat Sessions',
  },
  jsxgraph: {
    viewer: JSXGraphViewer,
    icon: { component: FunctionsIcon, props: { color: 'secondary' } },
    handler: {
      needsFullFetch: (resource) => !resource.content?.code,
    }
  },
};

Object.entries(CORE_CONTENT_TYPES).forEach(([type, config]) => {
  CONTENT_TYPE_REGISTRY.set(type, { ...CORE_CONTENT_TYPES.default, ...config });
});

/**
 * Registers or overrides a content type configuration.
 * @param {string} type - The content type identifier.
 * @param {object} config - The configuration object for this type.
 * @param {React.ComponentType} [config.viewer] - The viewer component.
 * @param {object} [config.icon] - Icon config { component, props }.
 * @param {function} [config.getTitle] - Function to get the resource title.
 * @param {object} [config.handler] - Data fetching handlers.
 */
export const registerContentType = (type, config) => {
  if (!type || !config) {
    console.error('Invalid content type registration:', { type, config });
    return;
  }
  const normalizedType = String(type).toLowerCase();
  const existingConfig = CONTENT_TYPE_REGISTRY.get(normalizedType) || CORE_CONTENT_TYPES.default;

  // --- START OF FIX ---
  const mergedConfig = {
    ...existingConfig,
    ...config,
    icon: { ...existingConfig.icon, ...(config.icon || {}) },
    handler: {
      ...CORE_CONTENT_TYPES.default.handler, // 1. Start with the default handler
      ...(existingConfig.handler || {}),     // 2. Layer the core-specific handler
      ...(config.handler || {}),             // 3. Layer the app-specific handler
    },
  };
  // --- END OF FIX ---

  CONTENT_TYPE_REGISTRY.set(normalizedType, mergedConfig);
};

/**
 * Retrieves the full configuration for a given content type.
 * @param {string} type - The content type identifier.
 * @returns {object} The configuration object for the type, or the default config if not found.
 */
export const getContentTypeConfig = (type) => {
  const normalizedType = type?.toLowerCase() || 'default';
  return CONTENT_TYPE_REGISTRY.get(normalizedType) || CONTENT_TYPE_REGISTRY.get('default');
};